import { HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStateManager } from '@features/auth/services/auth-state-manager-service';
import { AuthService } from '@features/auth/services/auth-service';
import { catchError, switchMap, throwError } from 'rxjs';
import { JwtUtils } from '@core/utils/jwt-utils';
import { DinnoHttpError } from '@core/models/dinno-http-error.model';

import { logger } from '@core/utils/logger';
export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authState = inject(AuthStateManager);
  const authService = inject(AuthService);

  // 1. Verificar si es una ruta pública (no requiere autenticación)
  if (isPublicRoute(req.url)) {
    return next(req);
  }

  // 2. Verificar si es una ruta que NO debe hacer refresh automático
  if (isExcludedFromRefresh(req.url)) {
    const token = authState.accessToken();
    if (!token) {
      return next(req);
    }

    const authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
    return next(authReq);
  }

  // 3. Obtener el token de acceso desde el estado
  const token = authState.accessToken();

  // 4. Si no hay token disponible, continuar sin autorización
  if (!token) {
    return next(req);
  }

  // 5. Caso especial: Scan QR como invitado
  if (isScanQrRoute(req.url) && authState.isGuest()) {
    return next(req);
  }

  // 6. NUEVO: Verificar si el token expira pronto (menos de 1 minuto)
  if (willExpireSoon(token, 60 * 1000)) {
    logger.warn('HTTP', 'El token vence pronto: se renueva antes');

    return authService.refreshAccessToken().pipe(
      switchMap((newAccessToken: string) => {
        const requestWithNewToken = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`),
        });
        return next(requestWithNewToken);
      }),
      catchError(() => {
        logger.error('HTTP', 'Falló la renovación anticipada: se usa el token actual');
        // Si falla el refresh, intentamos con el token actual de todos modos
        const authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`),
        });
        return next(authReq);
      }),
    );
  }

  // 7. Clonar la petición y agregar el header de autorización
  const authReq = req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
  });

  // 8. LÓGICA DE MANEJO DE ERRORES (COMPLETA)
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // CASO 0: Ignorar 401 en /login
      if (error.status === 401 && req.url.includes('/auth/login')) {
        return throwError(() => error);
      }

      // CASO 1: Es un error 401 en cualquier otra ruta
      // Todas las peticiones que fallan juntas esperan el mismo refresh (AuthService lo comparte)
      // y se reintentan con el token nuevo; ninguna se descarta.
      if (error.status === 401) {
        logger.warn('HTTP', 'Token vencido: se renueva');

        return authService.refreshAccessToken().pipe(
          // Sólo el fallo del refresh es "sesión expirada": un error del reintento se propaga tal cual
          catchError(() => {
            logger.error('HTTP', 'Falló la renovación del token: se cierra la sesión');
            return throwError(
              () =>
                new DinnoHttpError({
                  kind: 'auth',
                  title: 'Sesión expirada',
                  message: 'No pudimos renovar tu sesión. Por favor, ingresá de nuevo.',
                  retryable: false,
                  status: 401,
                }),
            );
          }),
          switchMap((newAccessToken: string) => {
            const requestWithNewToken = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`),
            });
            return next(requestWithNewToken);
          }),
        );
      }

      // Si no es 401, simplemente relanza el error
      return throwError(() => error);
    }),
  );
}

function isPublicRoute(url: string): boolean {
  const publicRoutes = [
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/public/',
    '/countries',
    '/provinces',
    '/cities',
  ];

  return publicRoutes.some((route) => url.includes(route));
}

function isScanQrRoute(url: string): boolean {
  return url.includes('/scan-qr');
}

function isExcludedFromRefresh(url: string): boolean {
  const excludedRoutes = [
    '/auth/refresh', // No refrescar cuando estamos refrescando
    '/auth/logout', // No refrescar cuando estamos cerrando sesión
    '/auth/login', // No refrescar en login
  ];

  return excludedRoutes.some((route) => url.includes(route));
}

function willExpireSoon(jwt: string, msBefore: number): boolean {
  try {
    const decoded = JwtUtils.decodeJWT(jwt);
    const expSec = decoded?.exp;
    if (!expSec) return true;

    const expMs = expSec * 1000;
    return Date.now() >= expMs - msBefore;
  } catch {
    return true;
  }
}
